import { defineStore } from 'pinia';
import { hasClientPortalPermission } from '~/lib/clientPortalPermissions';
import { toMillis } from '~/lib/timestamps';
import type { Approval } from '~/types/models';
import { buildClientPortalDataScopeKey, isCurrentClientPortalDataScope } from '~/lib/clientPortalDataScope';

export const useClientPortalApprovalsStore = defineStore('clientPortalApprovals', {
    state: () => ({
        approvals: [] as Approval[],
        scopeKey: null as string | null,
        loading: false,
        error: null as string | null,
    }),

    actions: {
        _sort(items: Approval[]): Approval[] {
            return [...items].sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
        },

        async _authHeaders(): Promise<Record<string, string>> {
            const { $auth } = useNuxtApp();
            const authUser = $auth.currentUser;

            if (!authUser) {
                throw new Error('You must be signed in to load approvals.');
            }

            return {
                authorization: `Bearer ${await authUser.getIdToken()}`,
            };
        },

        async fetchForClient(tenantId: string, clientId: string): Promise<void> {
            const portalStore = useClientPortalStore();
            const scopeKey = buildClientPortalDataScopeKey({ tenantId, clientId, previewClientId: portalStore.previewingAsClientId });
            if (this.scopeKey !== scopeKey) {
                this.approvals = [];
                this.scopeKey = scopeKey;
            }
            this.loading = true;
            this.error = null;

            try {
                if (!hasClientPortalPermission(portalStore.currentAccess, 'viewApprovals')) {
                    this.approvals = [];
                    return;
                }

                const response = await $fetch<{ approvals: Approval[] }>('/api/client-portal/approvals', {
                    query: {
                        tenantId,
                        clientId,
                        ...(portalStore.previewingAsClientId ? { preview: '1' } : {}),
                    },
                    headers: await this._authHeaders(),
                });

                if (isCurrentClientPortalDataScope(this.scopeKey, scopeKey)) {
                    this.approvals = this._sort(Array.isArray(response.approvals) ? response.approvals : []);
                }
            } catch (error) {
                if (!isCurrentClientPortalDataScope(this.scopeKey, scopeKey)) return;
                this.error = `Error loading approvals: ${error}`;
                console.error(this.error);
                this.approvals = [];
            } finally {
                if (isCurrentClientPortalDataScope(this.scopeKey, scopeKey)) this.loading = false;
            }
        },

        async respond(
            tenantId: string,
            approvalId: string,
            status: 'approved' | 'rejected',
            note?: string,
        ): Promise<{ projectCompleted: boolean }> {
            const { $auth } = useNuxtApp();
            const portalStore = useClientPortalStore();
            const scopeKey = this.scopeKey;
            if (!hasClientPortalPermission(portalStore.currentAccess, 'respondApprovals')) {
                throw new Error('You do not have permission to respond to approvals.');
            }

            this.error = null;

            const authUser = $auth.currentUser;
            if (!authUser?.uid) {
                throw new Error('You must be logged in to respond to approvals.');
            }

            const response = await $fetch<{ approval: Approval; projectCompleted?: boolean }>('/api/client-portal/approvals/respond', {
                method: 'POST',
                headers: await this._authHeaders(),
                body: {
                    tenantId,
                    clientId: portalStore.currentClient?.id || '',
                    approvalId,
                    status,
                    note,
                    ...(portalStore.previewingAsClientId ? { preview: '1' } : {}),
                },
            });

            if (!scopeKey || this.scopeKey !== scopeKey) {
                return { projectCompleted: Boolean(response.projectCompleted) };
            }

            const fallbackApproval = {
                ...(this.approvals.find((approval) => approval.id === approvalId) as Approval),
                status,
                reviewNote: note?.trim() || '',
                reviewedBy: authUser.uid,
                reviewedByName: authUser.displayName || authUser.email || 'Client',
                respondedAt: new Date(),
            };

            this.approvals = this.approvals.map((approval) =>
                approval.id === approvalId
                    ? response.approval || fallbackApproval
                    : approval,
            );
            this.approvals = this._sort(this.approvals);
            return { projectCompleted: Boolean(response.projectCompleted) };
        },
    },
});
